/**
 * What a navigation badge prints for a count — digits, or a Japanese thousands marker once digits
 * stop fitting. 日本-403.
 *
 * ## Why abbreviate at all
 *
 * The rail's badge is **the only element in the app with a hard width ceiling**: `--rail-item-w` is
 * 30px, and the pill inside it is 24px. At its 12px IBM Plex Mono 600 a digit advances 0.6em =
 * 7.2px, so three digits are 21.6px and fit, and **four are 28.8px and spill out of the pill's own
 * red fill**. That overflow is what reads as broken — not the flag, which the digits stay inside.
 *
 * It is reachable in ordinary use rather than a pathological case: the badge shows limit-capped
 * `dueToday` for the vocab deck, which holds ~8,958 cards, so anything up to four digits is what a
 * fortnight away produces.
 *
 * ## The form, and why it keeps the multiplier
 *
 * `1千+`, not a bare `千+` (Alex, 16 Aug 2026). The two cost 26.4px and 19.2px, and the 7.2px buys
 * the difference between a long evening and a backlog that got away — `千+` renders identically at
 * 1,000 and at 8,900, which is the range this deck can actually reach.
 *
 * **ASCII `+`, never full-width `＋`.** In the mono face ASCII advances 7.2px and full-width 12px,
 * so `1千＋` measures 31.2px and is over the 30px flag. The one character that must be full width is
 * 千 itself, which is why `--font-mono-jp` exists — see the token.
 *
 * ## The invariant
 *
 * **No output is longer than three characters, for any input.** That, plus the badge's
 * `min-w-[24px]`, is what makes clipping unrepresentable rather than merely unlikely — the caller
 * cannot hand this a number that produces a fourth glyph. `badgeCount.test.ts` pins it across the
 * range rather than at sampled points, because the whole failure being fixed here is a width nobody
 * measured.
 *
 * The 万 branch is what keeps the function total without clamping to something false. It is not
 * reachable from today's badge — the whole 日本語 tree is 11,302 cards — but a rule that runs out at
 * 9,999 would have to answer with a lie, and `9千+` for 12,000 is one. Clamping the *multiplier* at
 * 9 is not: `9万+` means "90,000 and up", which stays true however far up it goes.
 */
export function formatBadgeCount(count: number): string {
  if (count < 1000) return String(count);

  if (count < 10_000) {
    const thousands = Math.floor(count / 1000);
    return `${thousands}千+`;
  }

  // Clamped so the string cannot reach a fourth character. Truthful under the clamp because of the
  // `+`, which is the whole reason this reads `9万+` rather than a rounded figure.
  const myriads = Math.min(9, Math.floor(count / 10_000));
  return `${myriads}万+`;
}
